import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('valoriza_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('valoriza_token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.put('/auth/me/password', data),
};

// Vacations
export const vacationApi = {
  list: (params) => api.get('/vacations', { params }),
  create: (data) => api.post('/vacations', data),
  approve: (id, data) => api.put(`/vacations/${id}/approve`, data),
  reject: (id, data) => api.put(`/vacations/${id}/reject`, data),
  cancel: (id, reason) => api.put(`/vacations/${id}/cancel`, { reason: reason || '' }),
  approveCancel: (id, data) => api.put(`/vacations/${id}/approve-cancel`, data),
  rejectCancel: (id, data) => api.put(`/vacations/${id}/reject-cancel`, data),
  // Admin endpoints
  adminGetEmployee: (userId) => api.get(`/vacations/admin/employee/${userId}`),
  adminRecord: (data) => api.post('/vacations/admin/record', data),
  adminCancel: (reqId) => api.delete(`/vacations/admin/record/${reqId}`),
  teamWeekly: (weeks_ahead = 4) => api.get('/vacations/team-weekly', { params: { weeks_ahead } }),
};

// Remote work
export const remoteApi = {
  list: (params) => api.get('/remote-work', { params }),
  create: (data) => api.post('/remote-work', data),
  approve: (id, data) => api.put(`/remote-work/${id}/approve`, data),
  reject: (id, data) => api.put(`/remote-work/${id}/reject`, data),
  cancel: (id, reason) => api.put(`/remote-work/${id}/cancel`, { reason: reason || '' }),
  approveCancel: (id, data) => api.put(`/remote-work/${id}/approve-cancel`, data),
  rejectCancel: (id, data) => api.put(`/remote-work/${id}/reject-cancel`, data),
  monthlyBalance: (year, month, userId) => api.get('/remote-work/monthly-balance', { params: { year, month, user_id: userId } }),
  fixMonthlyBalances: () => api.post('/remote-work/fix-monthly-balances'),
  getAnnualPlan: (year) => api.get('/remote-work/annual-plan', { params: { year } }),
  approveAnnualPlan: (id, data) => api.put(`/remote-work/annual-plan/${id}/approve`, data),
  rejectAnnualPlan: (id, data) => api.put(`/remote-work/annual-plan/${id}/reject`, data),
  createAnnualPlan: (data) => api.post('/remote-work/annual-plan', data),
  teamCalendar: (year, month) => api.get('/remote-work/team-calendar', { params: { year, month } }),
  teamWeekly: (weeks_ahead = 3) => api.get('/remote-work/team-weekly', { params: { weeks_ahead } }),
};

// Users
export const userApi = {
  list: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  get: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getBalance: (id) => api.get(`/users/${id}/balance`),
  updateBalance: (id, data) => api.put(`/users/${id}/balance`, data),
};

// Teams
export const teamApi = {
  list: () => api.get('/teams'),
  create: (data) => api.post('/teams', data),
  update: (id, data) => api.put(`/teams/${id}`, data),
  delete: (id) => api.delete(`/teams/${id}`),
};

// Calendar
export const calendarApi = {
  events: (params) => api.get('/calendar/events', { params }),
  holidays: (year) => api.get('/calendar/holidays', { params: { year } }),
  allHolidays: (year) => api.get('/calendar/holidays/all', { params: { year } }),
  addHoliday: (data) => api.post('/calendar/holidays', data),
  updateHoliday: (id, data) => api.put(`/calendar/holidays/${id}`, data),
  deleteHoliday: (id) => api.delete(`/calendar/holidays/${id}`),
  breakdown: (start_date, end_date) => api.get('/calendar/day-breakdown', { params: { start_date, end_date } }),
};

export const calendarsMgmtApi = {
  list: () => api.get('/calendars'),
  getHolidays: (code, year) => api.get(`/calendars/${encodeURIComponent(code)}/holidays`, { params: year ? { year } : {} }),
  clone: (code, targetCode) => api.post(`/calendars/${encodeURIComponent(code)}/clone`, { target_code: targetCode }),
  addHoliday: (code, data) => api.post(`/calendars/${encodeURIComponent(code)}/holidays`, data),
  updateHoliday: (code, id, data) => api.put(`/calendars/${encodeURIComponent(code)}/holidays/${id}`, data),
  deleteHoliday: (code, id) => api.delete(`/calendars/${encodeURIComponent(code)}/holidays/${id}`),
  deleteCalendar: (code) => api.delete(`/calendars/${encodeURIComponent(code)}`),
  autoCreateMissing: () => api.post('/calendars/auto-create-missing'),
  auditUsers: () => api.get('/calendars/audit/users'),
  bulkAssign: (user_ids, calendar_code) => api.post('/calendars/audit/bulk-assign', { user_ids, calendar_code }),
};

// Reports
export const reportApi = {
  summary: (params) => api.get('/reports/summary', { params }),
  generateAI: (data) => api.post('/reports/ai', data),
  exportExcel: (params) => api.get('/reports/export/excel', { params, responseType: 'blob' }),
  auditLogs: () => api.get('/reports/audit-logs'),
  weeklyPreview: () => api.get('/reports/weekly-summary/preview'),
  sendWeeklySummary: () => api.post('/reports/weekly-summary'),
  downloadFunctionalGuide: () => api.get('/reports/user-manual', { responseType: 'blob' }),
  downloadUserGuide: () => api.get('/reports/user-manual', { responseType: 'blob' }),
  downloadAdminManual: () => api.get('/reports/admin-manual', { responseType: 'blob' }),
  teamAlerts: () => api.get('/reports/team-alerts'),
  teamMonthlyDetail: (year, month) => api.get('/reports/team-monthly-detail', { params: { year, month } }),
  vacationByDepartment: () => api.get('/reports/vacation-by-department'),
  exportVacationByDepartment: () => api.get('/reports/vacation-by-department/export', { responseType: 'blob' }),
  teamBalances: (managerId) => api.get('/reports/team-balances', { params: managerId ? { manager_id: managerId } : {} }),
  exportTeamBalances: (managerId) => api.get('/reports/team-balances/export', { params: managerId ? { manager_id: managerId } : {}, responseType: 'blob' }),
  contactHR: (message) => api.post('/reports/contact-hr', { message }),
};

// Settings
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};


// Work Clock
export const clockApi = {
  punch: (data) => api.post('/clock/punch', data || {}),
  today: () => api.get('/clock/today'),
  myHistory: (params) => api.get('/clock/my-history', { params }),
  teamToday: () => api.get('/clock/team/today'),
  teamHistory: (params) => api.get('/clock/team/history', { params }),
  teamSummary: (params) => api.get('/clock/team/summary', { params }),
};

// Sync
export const syncApi = {
  triggerSftp: () => api.post('/sync/trigger-sftp'),
  logs: (limit = 10) => api.get('/sync/logs', { params: { limit } }),
  sftpDiag: () => api.get('/sync/sftp-ls'),
  serverIp: () => api.get('/sync/server-ip'),
};

// Convenios (Asuntos Propios)
export const conveniosApi = {
  list: () => api.get('/convenios'),
  create: (data) => api.post('/convenios', data),
  update: (id, data) => api.put(`/convenios/${id}`, data),
  remove: (id) => api.delete(`/convenios/${id}`),
  assignUsers: (id, user_ids, convenio_id = null) => api.post(`/convenios/${id}/assign`, { user_ids, convenio_id }),
  listUsers: (id) => api.get(`/convenios/${id}/users`),
};

export const personalDaysApi = {
  list: (params) => api.get('/personal-days', { params }),
  balance: (params) => api.get('/personal-days/balance', { params }),
  create: (data) => api.post('/personal-days', data),
  approve: (id, comment) => api.put(`/personal-days/${id}/approve`, { comment }),
  reject: (id, comment) => api.put(`/personal-days/${id}/reject`, { comment }),
  cancel: (id) => api.put(`/personal-days/${id}/cancel`),
};

// LOGA Export
export const loGAApi = {
  preview: (start_date, end_date) => api.get('/loga/preview', { params: { start_date, end_date } }),
  export: (start_date, end_date) => api.get('/loga/export', { params: { start_date, end_date }, responseType: 'blob' }),
  sendEmail: (start_date, end_date) => api.post('/loga/send-email', null, { params: { start_date, end_date } }),
  captioStatus: () => api.get('/loga/captio-status'),
  captioImport: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/loga/captio-import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// Team overrides (manager team exceptions)
export const teamOverridesApi = {
  list: () => api.get('/admin/team-overrides'),
  upsert: (manager_email, extra_member_emails) =>
    api.post('/admin/team-overrides', { manager_email, extra_member_emails }),
  remove: (manager_email) => api.delete(`/admin/team-overrides/${encodeURIComponent(manager_email)}`),
};

// Manual user-field overrides (locks against CSV syncs)
export const userOverridesApi = {
  list: () => api.get('/admin/user-overrides'),
  upsert: (user_email, fields) => api.post('/admin/user-overrides', { user_email, fields }),
  remove: (user_email) => api.delete(`/admin/user-overrides/${encodeURIComponent(user_email)}`),
  exportAll: () => api.get('/admin/user-overrides/export'),
  importMany: (items, replace_existing = true) => api.post('/admin/user-overrides/import', { items, replace_existing }),
};

// Directory
export const directoryApi = {
  stats: () => api.get('/directory/stats'),
  highlights: () => api.get('/directory/highlights'),
  filters: () => api.get('/directory/filters'),
  list: (params) => api.get('/directory', { params }),
  person: (userId) => api.get(`/directory/${userId}`),
  org: (userId) => api.get(`/directory/org/${userId}`),
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/directory/me/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deletePhoto: () => api.delete('/directory/me/photo'),
  photoUrl: (userId, version) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const v = version ? `?v=${encodeURIComponent(version)}` : '';
    return `${base}/api/directory/photo/${userId}${v}`;
  },
  // Magic link photo upload (no login)
  requestPhotoMagicLink: (email, embedKey) =>
    api.post(`/directory/embed/request-photo-upload?key=${encodeURIComponent(embedKey)}`, { email }),
  verifyPhotoToken: (token) => api.get(`/directory/photo-token/verify`, { params: { token } }),
  uploadPhotoWithToken: (token, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/directory/photo-token/upload?token=${encodeURIComponent(token)}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Admin
  adminEmbedKey: () => api.get('/directory/admin/embed-key'),
};

export default api;
